import { Button, Flex, Tooltip, theme } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { trackPageView } from '@/api/events';
import SwitchUser from '@/components/admin/SwitchUser';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { DASHBOARD } from '@/constants/routes';
import NotificationPopup from '@/features/notifications/NotificationPopup';
import ProfileMenuPopup from '@/features/profile/ProfileMenuPopup';
import { useUnreadCounts } from '@/hooks/queries/useUnreadCounts';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import LogoMark from '@/icons/logo-ufo.svg?react';
import LinksMenuPopup from '@/UI/Popups/LinksMenuPopup';
import { getLocalStorageItem } from '@/utils/localStorage';

import { getNavigationItems, isSelected } from './navigationConfig';

const Navigation = () => {
  const { token } = theme.useToken();
  const { user, isAdmin } = useCurrentUser();
  const { globalUnread } = useUnreadCounts({ enabled: Boolean(user) });
  const [t] = useTranslation();
  const location = useLocation();

  const isNotAdmin = !isAdmin;

  const trackPage = useCallback(
    (page: string) => {
      const path = page.split('?')[0];
      if (location.pathname !== path && isNotAdmin) {
        void trackPageView(path);
      }
    },
    [location.pathname, isNotAdmin],
  );

  const navItems = useMemo(
    () => (user ? getNavigationItems(user, t, globalUnread) : []),
    [user, t, globalUnread],
  );

  const isAdminUser = getLocalStorageItem(IS_ADMIN_KEY);
  const showSwitchUser = isAdmin || isAdminUser;

  return (
    <Flex
      vertical
      align="center"
      justify="space-between"
      gap={12}
      style={{
        minHeight: '100%',
        padding: `${token.paddingMD + 2}px 0`,
      }}
      data-testid="app-navigation"
    >
      <Link
        to={DASHBOARD}
        onClick={() => trackPage(DASHBOARD)}
        aria-label={t('HIREPORT')}
        style={{ display: 'flex' }}
      >
        <LogoMark width={32} height={32} />
      </Link>

      {user && (
        <>
          <Flex vertical align="center" gap={4}>
            {navItems.map((item) => {
              const selected = isSelected(
                navItems,
                item.key,
                location.pathname,
              );
              return (
                <Tooltip key={item.key} title={item.title} placement="right">
                  <Link
                    to={item.key}
                    onClick={() => trackPage(item.key)}
                    aria-label={item.title}
                  >
                    <Button
                      size="large"
                      variant={selected ? 'filled' : 'link'}
                      color={selected ? 'primary' : 'default'}
                      shape="circle"
                      icon={item.icon}
                      aria-label={item.title}
                    />
                  </Link>
                </Tooltip>
              );
            })}
          </Flex>

          <Flex vertical align="center" gap={4}>
            {!isAdmin && <NotificationPopup />}
            {showSwitchUser && <SwitchUser />}
            {!isAdmin && <LinksMenuPopup />}
            <ProfileMenuPopup />
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default Navigation;
